import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import {
  createFoodCategory,
  fetchFoodCategories,
  updateFoodCategory,
  type FoodCategory,
} from "../foodApi";
import { QueryError } from "../components/QueryError";
import { SimpleTable } from "../components/SimpleTable";

export function CategoriesView() {
  const queryClient = useQueryClient();
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryType, setNewCategoryType] = useState<"ingredient" | "menu">("ingredient");
  const [renameDrafts, setRenameDrafts] = useState<Record<number, string>>({});
  const q = useQuery({ queryKey: ["food", "categories"], queryFn: fetchFoodCategories });

  const createMutation = useMutation({
    mutationFn: () =>
      createFoodCategory({ name: newCategoryName.trim(), type: newCategoryType }),
    onSuccess: () => {
      setNewCategoryName("");
      queryClient.invalidateQueries({ queryKey: ["food", "categories"] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: { name?: string; isActive?: boolean } }) =>
      updateFoodCategory(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["food", "categories"] });
    },
  });

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-tribal-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading categories...
      </div>
    );
  }
  if (q.isError || !q.data) {
    return <QueryError message={`Failed to load categories: ${(q.error as Error).message}`} />;
  }

  const columns: { key: string; title: string; render: (r: FoodCategory) => ReactNode }[] = [
    {
      key: "name",
      title: "Name",
      render: (r) => (
        <input
          value={renameDrafts[r.id] ?? r.name}
          onChange={(e) =>
            setRenameDrafts((prev) => ({ ...prev, [r.id]: e.target.value }))
          }
          className="w-56 rounded-md border border-tribal-border bg-black/20 px-2 py-1 text-sm"
        />
      ),
    },
    { key: "type", title: "Type", render: (r) => r.type },
    { key: "active", title: "Active", render: (r) => (r.isActive ? "Yes" : "No") },
    { key: "linked", title: "Linked Records", render: (r) => r.linkedCount },
    {
      key: "actions",
      title: "Actions",
      render: (r) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() =>
              updateMutation.mutate({
                id: r.id,
                payload: { name: (renameDrafts[r.id] ?? r.name).trim() },
              })
            }
            className="rounded bg-white/5 px-2 py-1 text-xs text-tribal-muted hover:text-white"
          >
            Rename
          </button>
          <button
            type="button"
            onClick={() =>
              updateMutation.mutate({ id: r.id, payload: { isActive: !r.isActive } })
            }
            className="rounded bg-white/5 px-2 py-1 text-xs text-tribal-muted hover:text-white"
          >
            {r.isActive ? "Archive" : "Restore"}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <section className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
        <h3 className="text-sm font-semibold">Create category</h3>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            placeholder="Category name"
            className="w-64 rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
          />
          <select
            value={newCategoryType}
            onChange={(e) => setNewCategoryType(e.target.value as "ingredient" | "menu")}
            className="rounded-md border border-tribal-border bg-black/20 px-2 py-1.5 text-sm"
          >
            <option value="ingredient">ingredient</option>
            <option value="menu">menu</option>
          </select>
          <button
            type="button"
            disabled={!newCategoryName.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
            className="rounded-md bg-tribal-red px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
          >
            Add
          </button>
        </div>
      </section>
      <section className="rounded-lg border border-tribal-border bg-tribal-surface">
        <SimpleTable<FoodCategory> rows={q.data} empty="No categories found." columns={columns} />
      </section>
    </div>
  );
}
